import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';

import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { GuidedCourseCreationService } from '@services/guided-course-creation.service';
import { LoadingSpinnerComponent } from '@shared/loading-spinner/loading-spinner.component';
import { SuggestionChipComponent } from '@shared/suggestion-chip/suggestion-chip.component';
import {
  DOCUMENT_ERROR_KEYS,
  DOCUMENT_MODES,
  DOCUMENT_UPLOAD,
  DOCUMENT_UPLOAD_STATES,
  DocumentMode,
  DocumentUploadResponse,
  DocumentUploadState,
  DocumentUploadStatus,
  getDocumentErrorKey,
  getDocumentRejectionKey,
  validateDocumentFile,
} from '@core/constants/guided-course-creation.constants';

interface TopicChip {
  topic: string;
  used: boolean;
}

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatSlideToggleModule,
    TranslateModule,
    TooltipButtonComponent,
    LoadingSpinnerComponent,
    SuggestionChipComponent,
  ],
})
export class DocumentUploadComponent {
  readonly acceptedFileTypes = DOCUMENT_UPLOAD.ACCEPTED_FILE_TYPES;
  readonly messageParams = { size: DOCUMENT_UPLOAD.MAX_SIZE_LABEL };

  readonly sessionId = input.required<string | null>();
  readonly disabled = input(false);
  readonly currentTopic = input('');

  readonly statusChange = output<DocumentUploadStatus>();
  readonly topicSelected = output<string>();

  readonly selectedFile = signal<File | null>(null);
  readonly isUploading = signal(false);
  readonly pageCount = signal<number | null>(null);
  readonly suggestedTopics = signal<readonly string[]>([]);
  readonly documentId = signal<string | null>(null);
  readonly documentMode = signal<DocumentMode>(DOCUMENT_MODES.RELAXED);
  readonly errorKey = signal<string | null>(null);
  readonly removeErrorKey = signal<string | null>(null);
  readonly isRemoving = signal(false);

  readonly isUploadDisabled = computed(
    () => this.disabled() || !!this.selectedFile(),
  );

  readonly showModeToggle = computed(() => !!this.documentId());

  readonly isStrictMode = computed(
    () => this.documentMode() === DOCUMENT_MODES.STRICT,
  );

  readonly modeDescriptionKey = computed(
    () => `guidedCourseCreation.document.mode.${this.documentMode()}`,
  );

  readonly topicChips = computed<readonly TopicChip[]>(() =>
    this.suggestedTopics().map(topic => ({
      topic,
      used: this.currentTopic().includes(topic),
    })),
  );

  private readonly fileInput =
    viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  private readonly gccService = inject(GuidedCourseCreationService);
  private readonly destroyRef = inject(DestroyRef);

  private uploadSubscription: Subscription | null = null;

  openFilePicker(): void {
    this.fileInput().nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const [file] = (event.target as HTMLInputElement).files ?? [];

    const sessionId = this.sessionId();
    if (!file || this.selectedFile() || !sessionId) return;

    const rejectionReason = validateDocumentFile(file);
    if (rejectionReason) {
      this.clearAnalysis();
      this.selectedFile.set(file);
      this.errorKey.set(getDocumentRejectionKey(rejectionReason));
      this.emitStatus(DOCUMENT_UPLOAD_STATES.ERROR);
      return;
    }

    this.selectedFile.set(file);
    this.startUpload(sessionId, file);
  }

  onSelectTopic(topic: string): void {
    this.topicSelected.emit(topic);
  }

  onModeChange(strict: boolean): void {
    this.documentMode.set(
      strict ? DOCUMENT_MODES.STRICT : DOCUMENT_MODES.RELAXED,
    );
    this.emitStatus(DOCUMENT_UPLOAD_STATES.DONE);
  }

  onRemove(): void {
    const sessionId = this.sessionId();
    const documentId = this.documentId();

    if (!sessionId || !documentId) {
      this.clearDocument();
      return;
    }

    this.isRemoving.set(true);
    this.removeErrorKey.set(null);

    this.gccService
      .deleteDocument(sessionId, documentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isRemoving.set(false);
          this.clearDocument();
        },
        error: () => {
          this.isRemoving.set(false);
          this.removeErrorKey.set(DOCUMENT_ERROR_KEYS.REMOVE_FAILED);
        },
      });
  }

  private clearDocument(): void {
    this.uploadSubscription?.unsubscribe();
    this.fileInput().nativeElement.value = '';
    this.clearAnalysis();
    this.selectedFile.set(null);
    this.emitStatus(DOCUMENT_UPLOAD_STATES.IDLE);
  }

  private startUpload(sessionId: string, file: File): void {
    this.uploadSubscription?.unsubscribe();
    this.clearAnalysis();
    this.isUploading.set(true);
    this.emitStatus(DOCUMENT_UPLOAD_STATES.UPLOADING);

    this.uploadSubscription = this.gccService
      .uploadDocument(sessionId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.handleAnalysis(response),
        error: (error: HttpErrorResponse) => this.handleHttpError(error),
      });
  }

  // Deliberately keeps `selectedFile`: an upload clears the previous result
  // while the newly picked file stays on screen.
  private clearAnalysis(): void {
    this.documentId.set(null);
    this.documentMode.set(DOCUMENT_MODES.RELAXED);
    this.errorKey.set(null);
    this.removeErrorKey.set(null);
    this.pageCount.set(null);
    this.suggestedTopics.set([]);
    this.isUploading.set(false);
  }

  private emitStatus(state: DocumentUploadState): void {
    this.statusChange.emit({
      state,
      suggestedTopics: this.suggestedTopics(),
      documentMode: this.documentMode(),
    });
  }

  private handleAnalysis(response: DocumentUploadResponse): void {
    this.isUploading.set(false);
    this.documentId.set(response.id);
    this.pageCount.set(response.analysis?.page_count ?? null);
    this.suggestedTopics.set(response.analysis?.suggested_topics ?? []);
    this.emitStatus(DOCUMENT_UPLOAD_STATES.DONE);
  }

  private handleHttpError(error: HttpErrorResponse): void {
    this.isUploading.set(false);
    this.errorKey.set(getDocumentErrorKey(error));
    this.emitStatus(DOCUMENT_UPLOAD_STATES.ERROR);
  }
}
